import { JsonData, JsonSchema } from '../types';

export class SchemaDetector {
  static detectSchema(data: JsonData): JsonSchema {
    return this.analyzeValue(data);
  }

  private static analyzeValue(value: any): JsonSchema {
    if (value === null) {
      return { type: 'null' };
    }

    if (Array.isArray(value)) {
      const itemSchemas = value.map(item => this.analyzeValue(item));
      const itemsSchema = this.mergeSchemas(itemSchemas);
      
      return {
        type: 'array',
        items: itemsSchema,
        description: `Array of ${value.length} items`
      };
    }

    if (typeof value === 'object') {
      const properties: Record<string, JsonSchema> = {};
      const required: string[] = [];

      Object.entries(value).forEach(([key, val]) => {
        properties[key] = this.analyzeValue(val);
        if (val !== undefined && val !== null) {
          required.push(key);
        }
      });

      return {
        type: 'object',
        properties,
        required,
        description: `Object with ${Object.keys(properties).length} properties`
      };
    }

    const primitiveType = typeof value as 'string' | 'number' | 'boolean';
    const schema: JsonSchema = { type: primitiveType };

    // Add format detection for strings
    if (typeof value === 'string') {
      schema.format = this.detectStringFormat(value);
    }

    // Add range information for numbers
    if (typeof value === 'number') {
      schema.description = `Numeric value`;
    }

    return schema;
  }

  private static mergeSchemas(schemas: JsonSchema[]): JsonSchema {
    if (schemas.length === 0) {
      return { type: 'null' };
    }

    if (schemas.length === 1) {
      return schemas[0];
    }

    // Check if all schemas are the same type
    const types = Array.from(new Set(schemas.map(s => s.type)));
    
    if (types.length === 1) {
      const baseType = types[0];
      
      if (baseType === 'object') {
        const allProperties = new Map<string, JsonSchema[]>();
        const allRequired = new Set<string>();

        schemas.forEach(schema => {
          if (schema.properties) {
            Object.entries(schema.properties).forEach(([key, propSchema]) => {
              if (!allProperties.has(key)) {
                allProperties.set(key, []);
              }
              allProperties.get(key)!.push(propSchema);
            });
          }
          if (schema.required) {
            schema.required.forEach(req => allRequired.add(req));
          }
        });

        const mergedProperties: Record<string, JsonSchema> = {};
        allProperties.forEach((propSchemas, key) => {
          mergedProperties[key] = this.mergeSchemas(propSchemas);
        });

        return {
          type: 'object' as const,
          properties: mergedProperties,
          required: Array.from(allRequired),
          description: `Object with merged properties`
        };
      } else if (baseType === 'array') {
        const allItemSchemas = schemas
          .filter(s => s.items)
          .map(s => s.items!);
        
        const mergedItems = allItemSchemas.length > 0 
          ? this.mergeSchemas(allItemSchemas)
          : { type: 'null' as const };

        return {
          type: 'array' as const,
          items: mergedItems,
          description: 'Array with merged item schema'
        };
      }
    }

    // If types differ, return a flexible schema
    return {
      type: 'object' as const,
      description: 'Mixed type data',
      properties: {}
    };
  }

  private static detectStringFormat(value: string): string | undefined {
    // Date patterns
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/.test(value)) {
      return 'date-time';
    }
    
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return 'date';
    }

    // Email pattern
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return 'email';
    }

    // URL pattern
    try {
      new URL(value);
      return 'uri';
    } catch (error) {
      // Not a valid URL
    }

    // UUID pattern
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
      return 'uuid';
    }

    // Phone number pattern (basic)
    if (/^\+?[\d\s\-()]+$/.test(value) && value.length >= 10) {
      return 'phone';
    }

    // Hex color
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
      return 'color';
    }

    // IP address
    if (/^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(value)) {
      return 'ipv4';
    }

    if (/^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/.test(value)) {
      return 'ipv6';
    }

    return undefined;
  }

  static generateStatistics(data: JsonData): Record<string, any> {
    const stats: Record<string, any> = {
      totalKeys: 0,
      totalValues: 0,
      types: {} as Record<string, number>,
      maxDepth: 0,
      arraySizes: [] as number[],
      stringLengths: [] as number[],
      numericRanges: {
        min: Number.MAX_VALUE,
        max: Number.MIN_VALUE,
        sum: 0,
        count: 0
      }
    };

    this.analyzeStatistics(data, stats, 0);

    if (stats.numericRanges.count === 0) {
      delete stats.numericRanges;
    } else {
      stats.numericRanges.avg = stats.numericRanges.sum / stats.numericRanges.count;
    }

    return stats;
  }

  private static analyzeStatistics(value: any, stats: Record<string, any>, depth: number): void {
    stats.maxDepth = Math.max(stats.maxDepth, depth);

    if (value === null || value === undefined) {
      stats.types['null'] = (stats.types['null'] || 0) + 1;
      stats.totalValues++;
      return;
    }

    const type = typeof value;
    stats.types[type] = (stats.types[type] || 0) + 1;
    stats.totalValues++;

    if (Array.isArray(value)) {
      stats.arraySizes.push(value.length);
      value.forEach((item, index) => {
        if (typeof item === 'object' && item !== null) {
          stats.totalKeys++;
        }
        this.analyzeStatistics(item, stats, depth + 1);
      });
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([key, val]) => {
        stats.totalKeys++;
        this.analyzeStatistics(val, stats, depth + 1);
      });
    } else if (typeof value === 'string') {
      stats.stringLengths.push(value.length);
    } else if (typeof value === 'number') {
      stats.numericRanges.min = Math.min(stats.numericRanges.min, value);
      stats.numericRanges.max = Math.max(stats.numericRanges.max, value);
      stats.numericRanges.sum += value;
      stats.numericRanges.count++;
    }
  }
}
